if (!customElements.get('product-form')) {
  class ProductForm extends HTMLElement {
    constructor() {
      super();
      // Initialize on construction – you can also call this.init() in connectedCallback if desired.
      this.init();
    }
    
    init() {
      // Use the standard form element (or a fallback selector)
      this.form = this.querySelector('form') || this.querySelector('.js-product-form');
      if (!this.form) return;
      
      // Enable the variant ID input
      const variantInput = this.form.querySelector('[name="id"]');
      if (variantInput) variantInput.disabled = false;
      
      // Add submit event listener if applicable
      if ("no-js" !== (window.theme?.settings?.afterAtc || "")) {
        this.form.addEventListener("submit", this.handleSubmit.bind(this));
      }
      
      // Determine cart element – try cart-notification, else cart-drawer
      this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      // Also assign cartDrawer for the second implementation’s use
      this.cartDrawer = document.querySelector("cart-drawer");
      
      // Get the submit button (using either type="submit" or name="add")
      this.submitButton = this.querySelector('[type="submit"]') || this.querySelector('[name="add"]');
      if (this.submitButton) {
        this.submitButtonText = this.submitButton.querySelector('span');
        // Set aria-haspopup if a cart-drawer exists
        if (document.querySelector('cart-drawer')) {
          this.submitButton.setAttribute('aria-haspopup', 'dialog');
        }
      }
      
      // Option to hide errors based on a data attribute
      this.hideErrors = this.dataset.hideErrors === 'true';
    }
    
    async handleSubmit(evt) {
      // For forms with id "product-signup_form", do nothing.
      if (evt.target.id === "product-signup_form") return;
      
      evt.preventDefault();
      if (this.submitButton && this.submitButton.getAttribute("aria-disabled") === "true") return;
      
      // Optionally vibrate on add-to-cart if enabled
      if (window.theme && theme.settings && theme.settings.vibrateOnATC && window.navigator.vibrate) {
        window.navigator.vibrate(100);
      }
      
      // Clear any error message
      this.errorMsg = null;
      this.setErrorMsgState();
      
      // Disable the button and show loading states
      if (this.submitButton) {
        this.submitButton.setAttribute("aria-disabled", "true");
        this.submitButton.classList.add("loading");
        const spinner = this.querySelector('.loading__spinner');
        if (spinner) spinner.classList.remove("hidden");
      }
      
      // Prepare the form data
      const formData = new FormData(this.form);
      // Build a "sections" parameter
      let sectionsParam = "cart-icon-bubble";
      if (this.cartDrawer) {
        const sectionId = this.cartDrawer.closest(".shopify-section")?.id.replace("shopify-section-", "");
        if (sectionId) sectionsParam += `,${sectionId}`;
      }
      formData.append("sections_url", window.location.pathname);
      formData.append("sections", sectionsParam);
      
      // Prepare fetch configuration
      const config = {
        method: "POST",
        headers: {
          Accept: "application/javascript",
          "X-Requested-With": "XMLHttpRequest"
        },
        body: formData
      };
      
      try {
        // First, fetch current cart info
        const cartRes = await fetch(`${theme.routes.cart}.js`);
        if (!cartRes.ok) throw new Error(cartRes.status);
        const currentCart = await cartRes.json();
        
        // Add item to cart
        const addRes = await fetch(theme.routes.cartAdd, config);
        const addData = await addRes.json();
        let errorMsg = typeof addData.description === "string" ? addData.description : addData.message;
        if (addData.errors && typeof addData.errors === "object") {
          errorMsg = Object.entries(addData.errors)
            .map(([key, val]) => val.join(", "))
            .join(" ");
        }
        if (addData.status) {
          this.setErrorMsgState(errorMsg);
          if (this.submitButton) {
            this.submitButton.setAttribute("aria-disabled", "true");
            if (this.submitButtonText) this.submitButtonText.classList.add("hidden");
            const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
            if (soldOutMessage) soldOutMessage.classList.remove("hidden");
          }
          this.error = true;
          return;
        } else if (!this.cart && !this.cartDrawer) {
          window.location = window.routes.cart_url;
          return;
        }
        
        // Create a marker and publish cart update event
        const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
        if (!this.error) {
          await publish(PUB_SUB_EVENTS.cartUpdate, {
            source: "product-form",
            productVariantId: formData.get("id"),
            cartData: addData
          });
          CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
        }
        this.error = false;
        
        // If within a quick-add modal, hide it after updating the cart
        const quickAddModal = this.closest("quick-add-modal");
        if (quickAddModal) {
          document.body.addEventListener("modalClosed", () => {
            setTimeout(() => {
              CartPerformance.measure("add:paint-updated-sections", () => {
                if (this.cartDrawer) {
                  this.cartDrawer.renderContents(addData);
                } else if (this.cart) {
                  this.cart.renderContents(addData);
                }
              });
            });
          }, { once: true });
          quickAddModal.hide(true);
        } else {
          CartPerformance.measure("add:paint-updated-sections", () => {
            if (this.cartDrawer) {
              this.cartDrawer.renderContents(addData);
            } else if (this.cart) {
              this.cart.renderContents(addData);
            }
          });
        }
        
        // Fetch updated cart info and dispatch an event for cart update
        const cartRes2 = await fetch(`${theme.routes.cart}.js`);
        if (!cartRes2.ok) throw new Error(cartRes2.status);
        const updatedCart = await cartRes2.json();
        const addedItem = currentCart.items.find(item => item.variant_id === addData.variant_id);
        if (addedItem) {
          this.dispatchEvent(new CustomEvent("on:line-item:change", {
            bubbles: true,
            detail: {
              cart: updatedCart,
              variantId: addData.variant_id,
              oldQuantity: addedItem.quantity,
              newQuantity: (addedItem.quantity === addData.quantity) ? addedItem.quantity : addData.quantity
            }
          }));
        } else {
          this.dispatchEvent(new CustomEvent("on:cart:add", {
            bubbles: true,
            detail: {
              cart: updatedCart,
              variantId: addData.variant_id
            }
          }));
        }
      } catch (error) {
        console.error(error);
        this.dispatchEvent(new CustomEvent("on:cart:error", {
          bubbles: true,
          detail: { error: this.errorMsg?.textContent }
        }));
        if (this.cartDrawer) this.cartDrawer.refresh(true);
      } finally {
        if (this.submitButton) {
          this.submitButton.classList.remove("loading");
          this.submitButton.classList.add("is-success");
          this.submitButton.removeAttribute("aria-disabled");
          setTimeout(() => {
            this.submitButton.classList.remove("is-loading");
            this.submitButton.classList.remove("is-success");
          }, 1400);
        }
        const spinner = this.querySelector('.loading__spinner');
        if (spinner) spinner.classList.add("hidden");
        CartPerformance.measureFromEvent("add:user-action", evt);
      }
    }
    
    static updateCartIcon(data) {
      const icon = document.getElementById("cart-icon-bubble");
      if (icon) icon.innerHTML = data.sections["cart-icon-bubble"];
    }
    
    setErrorMsgState(message = false) {
      this.errorMsg = this.errorMsg || this.querySelector(".js-form-error");
      if (this.errorMsg) {
        this.errorMsg.hidden = !message;
        if (message) {
          this.errorMsg.innerHTML = "";
          (Array.isArray(message) ? message : [message]).forEach((msg, idx) => {
            if (idx > 0) this.errorMsg.insertAdjacentHTML("beforeend", "<br>");
            this.errorMsg.insertAdjacentText("beforeend", msg);
          });
        }
      }
    }
  }
  customElements.define("product-form", ProductForm);
}
